import React, { useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Chip
} from '@mui/material';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { HistorySaleRecord } from '../../models/seat';

/**
 * Props for the GetHistorySale component
 */
interface GetHistorySaleProps {
    /** Array of historical sales records */
    data: HistorySaleRecord[];
    /** Loading state indicator */
    loading?: boolean;
    /** Error message if any */
    error?: string | null;
    /** Original query siteId used to fetch the data (may contain prefix) */
    querySiteId?: string;
}

/**
 * GetHistorySale Component
 *
 * Displays historical sales data for a specific event in a table format.
 * Shows sold inventory records from the past 6 months.
 * Highlights records where the returned siteId differs from the query siteId in red text.
 *
 * @param props - Component props containing sales data, loading state, error state, and query siteId
 * @returns React component displaying historical sales in a table
 */
const GetHistorySale: React.FC<GetHistorySaleProps> = ({ data, loading, error, querySiteId }) => {
    const apiRef = useGridApiRef();

    // Show loading state
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error state
    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    // Show empty state
    if (!data || data.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">No historical sales found for this event.</Alert>
            </Box>
        );
    }

    /**
     * Formats currency values for display
     */
    const formatCurrency = (value?: number): string => {
        if (value === null || value === undefined) return '-';
        return `${value.toFixed(2)}`;
    };

    /**
     * Determines whether the record likely has a siteId mismatch.
     * Rows without an order_number are considered mismatched.
     *
     * @param row - The sale record from the database
     * @returns true if the row lacks an order_number (indicating a mismatch)
     */
    const isSiteIdMismatch = (row: HistorySaleRecord): boolean => {
        return row.siteid !== querySiteId;
    };

    const WrapContent = ({ value }: { value: string }) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {value}
        </div>
    );

    // Define column definitions for DataGrid
    const columns: GridColDef[] = [
        { field: 'order_number', headerName: 'Order', width: 100 },
        { field: 'siteid', headerName: 'Site ID', width: 100 },
        { field: 'event', headerName: 'Event', width: 150, flex: 1,
            renderCell: (params) => (
                <WrapContent value={params.value} />
            ),
        },
        { field: 'venue', headerName: 'Venue', width: 150, flex: 1,
            renderCell: (params) => (
                <WrapContent value={params.value} />
            ),
        },
        {
            field: 'event_date',
            headerName: 'Event Date',
            width: 100,
            // Custom cell renderer to display date on two lines: date on top, time below
            // Improves readability and consistency with design requirements
            renderCell: (params) => {
                const dateStr = params.row.event_date;
                if (!dateStr) return '-';
                
                try {
                    const date = dayjs.utc(dateStr);
                    return (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            height: '100%',
                            lineHeight: 1.4,
                        }}>
                            <div>{date.format('YYYY-MM-DD')}</div>
                            <div style={{ color: 'gray', fontSize: '0.85em' }}>{date.format('HH:mm A')}</div>
                        </div>
                    );
                } catch {
                    return dateStr;
                }
            }
        },
        {
            field: 'order_date',
            headerName: 'Sold Date',
            width: 100,
            // Custom cell renderer to display date on two lines: date on top, time below
            // Provides consistent date formatting across all date columns
            renderCell: (params) => {
                const dateStr = params.row.order_date;
                if (!dateStr) return '-';
                
                try {
                    const date = dayjs.utc(dateStr);
                    return (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            height: '100%',
                            lineHeight: 1.4,
                        }}>
                            <div>{date.format('YYYY-MM-DD')}</div>
                            <div style={{ color: 'gray', fontSize: '0.85em' }}>{date.format('HH:mm A')}</div>
                        </div>
                    );
                } catch {
                    return dateStr;
                }
            }
        },
        {
            field: 'listed_date',
            headerName: 'Relisted Date',
            width: 120,
            // Custom cell renderer to display date on two lines: date on top, time below
            // Additionally shows days difference calculation when order_date is available
            renderCell: (params) => {
                const listedDate = params.row.listed_date;
                const orderDate = params.row.order_date;
                
                // Handle null or undefined dates
                if (!listedDate) {
                    return '-';
                }
                
                try {
                    const date = dayjs.utc(listedDate);
                    let diffElement;
                    // Calculate days difference if both dates are available
                    if (orderDate) {
                        const orderDateParsed = dayjs.utc(orderDate);
                        
                        // Calculate the difference in days
                        // Positive value means orderDate is after listedDate (item was listed then sold)
                        const daysDiff = orderDateParsed.diff(date, 'day');
                        diffElement = (
                            <div style={{ color: 'gray', fontSize: '0.75em' }}>({daysDiff} days difference)</div>
                        );
                    }
                    return (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            height: '100%',
                            lineHeight: 1.4,
                        }}>
                            <div>{date.format('YYYY-MM-DD')}</div>
                            <div style={{ color: 'gray', fontSize: '0.85em' }}>{date.format('HH:mm A')}</div>
                            {diffElement}
                        </div>
                    );
                } catch {
                    // Return fallback if date parsing fails
                    return '-';
                }
            }
        },
        {
            field: 'quantity',
            headerName: 'Qty',
            width: 60,
            align: 'center',
            headerAlign: 'center'
        },
        { field: 'section', headerName: 'Section', width: 120,
            renderCell: (params) => (
                <WrapContent value={params.value} />
            )
        },
        { field: 'row', headerName: 'Row', width: 60 },
        { field: 'seats', headerName: 'Seats', width: 100 },
        {
            field: 'unitTicketSales',
            headerName: 'Unit Ticket Sales',
            width: 130,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value) => formatCurrency(value)
        },
        {
            field: 'cost',
            headerName: 'Cost',
            width: 80,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value) => formatCurrency(value)
        },
        {
            field: 'profit',
            headerName: 'Profit',
            width: 80,
            align: 'right',
            headerAlign: 'right',
            valueGetter: (value, row) => {
                const unitTicketSales = row.unitTicketSales;
                const cost = row.cost;
                if (cost === null || cost === undefined || isNaN(Number(cost)) ||
                    unitTicketSales === null || unitTicketSales === undefined || isNaN(Number(unitTicketSales))) {
                    return null;
                }
                return unitTicketSales - cost;
            },
            valueFormatter: (value) => formatCurrency(value)
        },
        {
            field: 'action',
            headerName: 'Status',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            // Custom cell renderer for status column with MUI Chips based on action field value
            // Displays color-coded status chips to visualize inventory lifecycle states
            renderCell: (params) => {
                const action = params.row.action;
                
                // Map action values to chip properties
                // action = 1: Active (green chip) - Item is currently listed for sale
                // action = 2: Expired (error chip) - Listing has expired
                // action = 0: Sold (warning chip) - Item has been sold
                switch (action) {
                    case 1:
                        return <Chip label="Active" color="success" size="small" />;
                    case 2:
                        return <Chip label="Expired" color="error" size="small" />;
                    case 0:
                        return <Chip label="Sold" color="warning" size="small" />;
                }
            }
        }
    ];

    useEffect(() => {
        if (!apiRef.current || !data || data.length === 0) return;

        // Check if any row's siteId differs from the parsed query siteId.
        // When a mismatch is detected (i.e., the record's siteId doesn't match the parsed querySiteId),
        // we show the siteId column to provide visual feedback about data discrepancies.
        // If all siteIds match, the column remains hidden to reduce clutter.
        const hasMismatch = data.some(row => {
            return isSiteIdMismatch(row);
        });

        apiRef.current.setColumnVisibilityModel({
            siteid: hasMismatch
        });
    }, [data, querySiteId, apiRef]);

    /**
     * Calculates the total profit from all records
     * Sum of all Unit Profit values across all rows
     * @returns Total profit value or 0 if no valid data
     */
    const calculateTotalProfit = (): number => {
        return data.reduce((sum, row) => {
            const cost = row.cost;
            const unitTicketSales = row.unitTicketSales;
            if (cost !== null && cost !== undefined && !isNaN(Number(cost)) &&
                unitTicketSales !== null && unitTicketSales !== undefined && !isNaN(Number(unitTicketSales))) {
                return sum + ((unitTicketSales - cost) * (row.quantity || 0));
            }
            return sum;
        }, 0);
    };

    /**
     * Calculates the total cost from all records
     * Sum of all Unit Cost values across all rows
     * @returns Total cost value or 0 if no valid data
     */
    const calculateTotalCost = (): number => {
        return data.reduce((sum, row) => {
            const cost = row.cost;
            if (cost !== null && cost !== undefined && !isNaN(Number(cost))) {
                return sum + (cost * (row.quantity || 0));
            }
            return sum;
        }, 0);
    };

    // Prepare rows with id field required by DataGrid
    const rows = data.map((row) => ({
        ...row,
        id: row.inventory_id
    }));

    /**
     * Custom footer component for the DataGrid displaying summary totals
     * This footer is fixed at the bottom and remains visible while scrolling
     */
    const CustomFooter = () => {
        return (
            <Box sx={{
                p: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderTop: '2px solid rgba(0, 0, 0, 0.12)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 4,
                position: 'sticky',
                bottom: 0,
                zIndex: 1
            }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Total Cost: <span style={{ color: '#d32f2f' }}>${calculateTotalCost().toFixed(2)}</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Total Profit: <span style={{ color: '#2e7d32' }}>${calculateTotalProfit().toFixed(2)}</span>
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
                Historical Sales Records ({data.length} records)
            </Typography>
            <Box sx={{ height: 400, width: '100%', position: 'relative' }}>
                <DataGrid
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                siteid: false
                            }
                        }
                    }}
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    getRowHeight={() => 'auto'}
                    getRowClassName={(params) => {
                        // Apply conditional styling to highlight rows with mismatched siteIds
                        // When the returned record's siteId differs from the query siteId,
                        // the row will be highlighted in red to indicate a data discrepancy
                        return isSiteIdMismatch(params.row) ? 'mismatched-siteid' : '';
                    }}
                    sx={{
                        height: '100%',
                        '& .MuiDataGrid-main': {
                            overflow: 'auto',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: 'background.paper',
                            borderTop: '2px solid',
                            borderColor: 'divider'
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',       // vertical center
                            whiteSpace: 'normal',       // wrap text
                            wordWrap: 'break-word',
                            lineHeight: '1.5',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            display: 'flex',
                            alignItems: 'center',       // header vertical center
                        },
                        // Style for rows with mismatched siteIds - display in red text
                        '& .mismatched-siteid': {
                            color: 'red',
                            '& .MuiDataGrid-cell': {
                                color: 'red'
                            }
                        }
                    }}
                    slots={{
                        footer: CustomFooter
                    }}
                />
            </Box>
        </Box>
    );
};

export default GetHistorySale;
